// src/pages/technicians/AdminTechniciansPage.tsx

import {
  AlertCircle,
  Copy,
  Eye,
  KeyRound,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface Technician {
  id: string;
  client_id: string;
  role_id: string;
  username: string;
  email?: string | null;
  external_access_token?: string | null;
  created_at?: string | null;
}

interface TechnicianCreateForm {
  username: string;
  email: string;
  password: string;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const normalizeList = <T,>(res: unknown): T[] => {
  if (Array.isArray(res)) return res as T[];

  if (res && typeof res === "object") {
    const obj = res as { items?: T[]; data?: T[]; results?: T[] };
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.results)) return obj.results;
  }

  return [];
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const maskToken = (token?: string | null) => {
  if (!token) return "No token";

  if (token.length <= 12) return token;

  return `${token.slice(0, 8)}••••••••${token.slice(-6)}`;
};

const getInitials = (name?: string | null) =>
  String(name || "T")
    .split(" ")
    .filter(Boolean)
    .map((item) => item[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "T";

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading technicians…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while technician accounts are being prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const TechnicianPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

  const [form, setForm] = useState<TechnicianCreateForm>({
    username: "",
    email: "",
    password: "",
  });

  const loadTechnicians = async (manual = false) => {
    try {
      if (manual) setRefreshing(true);
      else setLoading(true);

      setError(null);

      const res = await fetchApp("/admin/technicians/");
      setTechnicians(normalizeList<Technician>(res));
    } catch (err: any) {
      const msg = err?.message || "Failed to load technicians.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTechnicians(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTechnicians = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return technicians;

    return technicians.filter((technician) =>
      [
        technician.username,
        technician.email || "",
        technician.id,
        technician.external_access_token || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [technicians, search]);

  const stats = useMemo(() => {
    return {
      total: technicians.length,
      filtered: filteredTechnicians.length,
      withToken: technicians.filter((item) => item.external_access_token)
        .length,
      withoutToken: technicians.filter((item) => !item.external_access_token)
        .length,
    };
  }, [technicians, filteredTechnicians]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.username.trim() || !form.email.trim() || !form.password.trim()) {
      showToast("Username, email and password are required.", "error");
      return;
    }

    
    try {
      setCreating(true);

      await fetchApp("/admin/technicians/", {
        method: "POST",
        body: JSON.stringify({
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
        }),
      });

      showToast("Technician created successfully.", "success");
      setCreateOpen(false);
      setForm({ username: "", email: "", password: "" });
      await loadTechnicians(true);
    } catch (err: any) {
      showToast(err?.message || "Failed to create technician.", "error");
    } finally {
      setCreating(false);
    }
  };

  const regenerateToken = async (technician: Technician) => {
    const ok = window.confirm(
      `Regenerate external token for ${technician.username}? The old token will stop working.`,
    );

    if (!ok) return;

    try {
      setRegeneratingId(technician.id);

      const updated = await fetchApp<Technician>(
        `/admin/technicians/${technician.id}/regenerate-token`,
        {
          method: "POST",
        },
      );

      setTechnicians((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );

      showToast("Technician token regenerated successfully.", "success");
    } catch (err: any) {
      showToast(err?.message || "Failed to regenerate token.", "error");
    } finally {
      setRegeneratingId(null);
    }
  };

  const copyToken = async (token?: string | null) => {
    if (!token) {
      showToast("No token available.", "error");
      return;
    }

    try {
      await navigator.clipboard.writeText(token);
      showToast("Token copied to clipboard.", "success");
    } catch {
      showToast("Failed to copy token.", "error");
    }
  };

  if (loading && technicians.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Technician Management
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Technicians
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Create technician accounts, manage external access tokens, and
                  view assigned support tickets.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => loadTechnicians(true)}
                  disabled={refreshing}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>

                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  New Technician
                </button>
              </div>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing technicians…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Total"
            value={stats.total}
            sub="Technician accounts"
            icon={<Users className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Showing"
            value={stats.filtered}
            sub="Filtered technicians"
            icon={<Search className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="With Token"
            value={stats.withToken}
            sub="External access enabled"
            icon={<KeyRound className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="No Token"
            value={stats.withoutToken}
            sub="Needs token regeneration"
            icon={<ShieldCheck className="h-5 w-5" />}
            tone={stats.withoutToken > 0 ? "amber" : "slate"}
          />
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[14px] font-black text-slate-900">
                Technician List
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Showing {filteredTechnicians.length} of {technicians.length}{" "}
                technicians
              </p>
            </div>

            <div className="relative w-full lg:w-96">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search username, email, token..."
                className={`${inputCls} pl-10`}
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Technician</th>
                  <th className="px-5 py-3.5 font-black">Email</th>
                  <th className="px-5 py-3.5 font-black">External Token</th>
                  <th className="px-5 py-3.5 font-black">Created</th>
                  <th className="px-5 py-3.5 text-right font-black">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {filteredTechnicians.length > 0 ? (
                  filteredTechnicians.map((technician) => (
                    <tr
                      key={technician.id}
                      className="cursor-pointer transition-colors hover:bg-emerald-50/60"
                      onClick={() =>
                        navigate(`/admin/technicians/${technician.id}`)
                      }
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[12px] font-black text-emerald-700 ring-1 ring-emerald-200">
                            {getInitials(technician.username)}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-black text-slate-900">
                              {technician.username}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                          <Mail className="h-3 w-3" />
                          {technician.email || "No email"}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className="max-w-[320px] truncate rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 font-mono text-[11px] font-black text-slate-600">
                            {maskToken(technician.external_access_token)}
                          </span>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToken(technician.external_access_token);
                            }}
                            className="rounded-xl p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                        {formatDateTime(technician.created_at)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              regenerateToken(technician);
                            }}
                            disabled={regeneratingId === technician.id}
                            className={`${btnBase} h-9 border border-amber-200 bg-amber-100 px-3 text-amber-800 hover:bg-amber-200`}
                          >
                            {regeneratingId === technician.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <KeyRound className="h-3.5 w-3.5" />
                            )}
                            Token
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/technicians/${technician.id}`);
                            }}
                            className={`${btnBase} h-9 border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <UserCheck className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No technicians found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        Create a technician account to assign support tickets.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {filteredTechnicians.length > 0 ? (
              filteredTechnicians.map((technician) => (
                <div key={technician.id} className="px-4 py-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-3xl bg-emerald-100 text-[13px] font-black text-emerald-700 ring-1 ring-emerald-200">
                      {getInitials(technician.username)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-black text-slate-900">
                        {technician.username}
                      </p>

                      <p className="mt-0.5 truncate text-[12px] font-medium text-slate-500">
                        {technician.email || "No email"}
                      </p>

                      <p className="mt-2 truncate font-mono text-[11px] font-bold text-slate-500">
                        {maskToken(technician.external_access_token)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => regenerateToken(technician)}
                      disabled={regeneratingId === technician.id}
                      className={`${btnBase} h-10 border border-amber-200 bg-amber-100 px-3 text-amber-800 hover:bg-amber-200`}
                    >
                      {regeneratingId === technician.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <KeyRound className="h-4 w-4" />
                      )}
                      Token
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/admin/technicians/${technician.id}`)
                      }
                      className={`${btnBase} h-10 border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200`}
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <UserCheck className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No technicians found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Create a technician account to assign support tickets.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
          <div className="w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
              <div>
                <p className="text-[15px] font-black text-slate-900">
                  Create Technician
                </p>

                <p className="mt-1 text-[12.5px] font-medium text-slate-500">
                  Create a technician login account and external access token.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
                className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Username
                </label>

                <input
                  value={form.username}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, username: e.target.value }))
                  }
                  placeholder="e.g. john_tech"
                  className={inputCls}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Email
                </label>

                <input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="technician@example.com"
                  className={inputCls}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Password
                </label>

                <input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, password: e.target.value }))
                  }
                  placeholder="Minimum 6 characters"
                  className={inputCls}
                />
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  disabled={creating}
                  className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-700 hover:bg-slate-100`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={creating}
                  className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {creating ? "Creating…" : "Create Technician"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianPage;
